import React, { FC, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import clsx from "clsx"
import { get } from "lodash"

import { useTranslation } from "react-i18next"

import { DataSourceBadge } from "components/badges/DataSourceBadge"
import { TableDropdown } from "components/dropdowns/TableDropdown"
import { NoData } from "components/NoData"
import { TablePagination } from "components/paginations/TablePagination"
import { isColumnSortable } from "components/tables/isColumnSortable"

import { tablePageSize } from "constants/data"
import { SortOrderBy, SortOrderType } from "constants/eums"
import { TableColumnsInterface } from "constants/interfaces"
import { TableHandler } from "constants/type"

import { isAuthorityUnavailableError } from "util/api"

import { AccordionTableCell } from "./AccordionTableCell"

type FiltersType = {
  [key: string]: any
}

type Props = {
  title: string
  handler: TableHandler
  columns: TableColumnsInterface[]
  id?: string | number
  idTrue?: boolean

  filters?: FiltersType
  defaultSortOrderBy?: SortOrderBy | string
  defaultSortOrder?: SortOrderType | string

  buttonHandler?: () => void
  buttonText?: string

  renderExpanded?: (row: any) => ReactNode
}

export const AccordionTable: FC<Props> = ({
  title,
  handler,
  columns,
  id,
  idTrue = false,
  filters,
  defaultSortOrderBy = SortOrderBy.CREATED_AT,
  defaultSortOrder = SortOrderType.DESC,
  buttonHandler,
  buttonText,
  renderExpanded
}): ReactElement => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const hasExpanded = !!renderExpanded

  const [openRowId, setOpenRowId] = useState<string | number | null>(null)

  const [rows, setRows] = useState<any[]>([])
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [totalPages, setTotalPages] = useState<number>(1)

  const [orderBy, setOrderBy] = useState<string>(String(defaultSortOrderBy))
  const [order, setOrder] = useState<string>(String(defaultSortOrder))

  const [loading, setLoading] = useState<boolean>(false)
  // SOFT-559: data-source marker + a fail-closed 503 state that keeps the already-rendered rows.
  const [dataSource, setDataSource] = useState<string | undefined>(undefined)
  const [loadError, setLoadError] = useState<null | "unavailable" | "generic">(null)

  const visibleCols = useMemo(() => columns, [columns])

  const onLoad = useCallback(
    async (nextPage: number, nextPageSize: number) => {
      if (idTrue && !id) return

      setLoading(true)
      setLoadError(null)
      try {
        const result = (await handler(nextPage, nextPageSize, id as any, filters as any, orderBy, order)) as any
        const data = result?.data ?? []

        setRows(data)
        setPage(result?.page ?? 1)
        setTotal(result?.total ?? 0)
        setTotalPages(result?.total_pages ?? 1)
        // SOFT-559: surface the ledger the backend served this window from (absent → non-MS tenant).
        setDataSource(result?.data_source)

        setOpenRowId(null)
      } catch (err: any) {
        // SOFT-559: fail-closed — a 503 (FS-era window, financial-service down) is retryable. Keep
        // the already-rendered rows instead of blanking the table, and show a retry affordance.
        setLoadError(isAuthorityUnavailableError(err) ? "unavailable" : "generic")
      } finally {
        setLoading(false)
      }
    },
    [handler, id, idTrue, filters, orderBy, order]
  )

  useEffect(() => {
    onLoad(page, pageSize)
  }, [page, pageSize, onLoad])

  const onJump = useCallback(
    (jumpTo: number) => {
      if (!jumpTo) return
      onLoad(jumpTo, pageSize)
    },
    [onLoad, pageSize]
  )

  const handleRequestSort = (value: string) => {
    if (!value) return

    if (value === orderBy) {
      setOrder(order === SortOrderType.ASC ? SortOrderType.DESC : SortOrderType.ASC)
      setPage(1)
      return
    }

    setOrderBy(value)
    setOrder(SortOrderType.ASC)
    setPage(1)
  }

  useEffect(() => {
    onLoad(1, pageSize)
  }, [orderBy, order, pageSize, onLoad])

  const cellBase = "py-3 px-3 text-sm text-[rgba(6,7,21,1)] dark:text-white"
  const borderRow = "border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.08)]"

  return (
    <div className={clsx("rounded-xl border overflow-x-auto", "border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)]")}>
      <div className={clsx("flex items-center justify-between gap-4 p-4", "bg-white dark:bg-[#0C0C1A]")}>
        <div className="flex items-center gap-3">
          <div className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">{title}</div>
          <DataSourceBadge source={dataSource} />
          {loading ? <span className="loading loading-spinner loading-sm text-gray-400" /> : null}
          {buttonHandler ? (
            <button className="btn btn-primary btn-sm" onClick={buttonHandler}>
              {buttonText}
            </button>
          ) : null}
        </div>

        {rows.length ? (
          <div className="flex items-center gap-2 text-[rgba(6,7,21,0.75)] dark:text-white/70">
            <label>{t("table.rows")}</label>
            <TableDropdown value={pageSize} onValueChange={(v: any) => setPageSize(v)} columns={tablePageSize} />
          </div>
        ) : null}
      </div>

      {loadError ? (
        <div
          className={clsx(
            "px-4 py-2 text-sm border-t",
            loadError === "unavailable"
              ? "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20"
              : "text-red-600 dark:text-red-400 bg-red-500/10 border-red-500/20"
          )}
        >
          {loadError === "unavailable" ? t("table.loadFailedUnavailable") : t("table.loadFailed")}
        </div>
      ) : null}

      <table className="w-full min-w-[900px] bg-white dark:bg-[#0C0C1A]">
        <thead className="bg-[rgba(139,92,246,0.06)] dark:bg-[rgba(139,92,246,0.08)]">
          <tr className="text-xs uppercase tracking-wide text-[rgba(6,7,21,0.6)] dark:text-white/70">
            {visibleCols.map((c) => {
              const sortable = isColumnSortable(c)
              let sortArrow: ReactNode = null
              if (sortable && orderBy === c.value) {
                let arrow = "▼"
                if (order === SortOrderType.ASC) {
                  arrow = "▲"
                }
                sortArrow = <span className="text-[10px]">{arrow}</span>
              }
              return (
                <th
                  key={c.value}
                  className={clsx("py-3 px-3 text-left", sortable && "cursor-pointer select-none")}
                  onClick={() => sortable && handleRequestSort(c.value)}
                >
                  <div className="inline-flex items-center gap-2">
                    <span>{c.title}</span>
                    {sortArrow}
                  </div>
                </th>
              )
            })}
            {hasExpanded ? <th className="py-3 px-3 text-right"> </th> : null}
          </tr>
        </thead>

        <tbody>
          {!rows.length ? (
            <tr>
              <td colSpan={visibleCols.length + (hasExpanded ? 1 : 0)} className="py-10 text-center">
                <NoData />
              </td>
            </tr>
          ) : null}

          {rows.map((row) => {
            const rowId = row?.id ?? row?.uuid ?? row?.code ?? row?.user_id
            const isOpen = openRowId === rowId
            const hasSelection = openRowId !== null

            return (
              <React.Fragment key={String(rowId)}>
                <tr
                  className={clsx(
                    borderRow,
                    hasExpanded && "cursor-pointer",
                    hasExpanded &&
                      !hasSelection &&
                      "hover:contrast-125 hover:bg-[rgba(139,92,246,0.2)] hover:dark:bg-[rgba(139,92,246,0.20)]",
                    hasSelection && !isOpen && "opacity-40 hover:opacity-60",
                    hasSelection && isOpen && "bg-[rgba(139,92,246,0.06)] dark:bg-[rgba(139,92,246,0.10)]"
                  )}
                  onClick={() => {
                    if (!hasExpanded) return
                    setOpenRowId(isOpen ? null : rowId)
                  }}
                >
                  {visibleCols.map((c) => {
                    const raw = get(row, c.value)
                    return (
                      <AccordionTableCell
                        key={c.value}
                        column={c}
                        rawValue={raw}
                        row={row}
                        className={clsx(
                          "text-[rgba(6,7,21,0.7)] dark:text-white/70",
                          hasSelection && !isOpen && "text-[rgba(6,7,21,0.45)] dark:text-white/40"
                        )}
                      />
                    )
                  })}

                  {hasExpanded ? (
                    <td className={clsx(cellBase, "text-right")}>
                      <span className="text-xs">{isOpen ? "▼" : "▶"}</span>
                    </td>
                  ) : null}
                </tr>

                {hasExpanded && isOpen ? (
                  <tr className={clsx(borderRow)}>
                    <td colSpan={visibleCols.length + 1} className="p-0">
                      <div
                        className={clsx(
                          "bg-[rgba(0,0,0,0.01)] dark:bg-white/5",
                          "border-t border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.08)]"
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {renderExpanded?.(row)}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>

      {rows.length ? (
        <div className="p-4 bg-white dark:bg-[#0C0C1A]">
          <TablePagination page={page} totalPages={totalPages} onJump={onJump} />
          <div className="mt-2 text-xs text-[rgba(6,7,21,0.5)] dark:text-white/50">{total ? `Total: ${total}` : null}</div>
        </div>
      ) : null}
    </div>
  )
}
